import React, { useState, useRef, useEffect } from "react";

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  className?: string;
  styles?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  className = "",
  styles = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const toggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((item) => item !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  return (
    <div className={`relative dark:bg-primary-800  z-10 ${className}`} ref={dropdownRef}>
      <div
        className={`border-[0.06rem] py-[0.69rem] px-[0.88rem] w-[15rem] rounded-[0.25rem] text-primary-400 dark:text-primary-300 dark:border-primary-800 text-sm flex items-center justify-between cursor-pointer ${styles} z-10`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>
          {selectedValues.length > 0
            ? options
                .filter((option) => selectedValues.includes(option.value))
                .map((option) => option.label)
                .join(", ")
            : placeholder}
        </span>
        <svg className={`w-4 h-4 transform ${isOpen ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      {isOpen && (
        <div className="absolute mt-1 w-[15rem] rounded-md bg-white dark:bg-primary-800 shadow-lg z-20">
          <ul className="max-h-60 overflow-auto text-sm text-primary-400 dark:text-primary-300">
            {options.map((option) => (
              <li
                key={option.value}
                className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-primary-900 cursor-pointer"
                onClick={() => toggleOption(option.value)}
              >
                <div className="flex items-center">
                  <span
                    className={`h-4 w-4 rounded-sm border flex items-center justify-center ${
                      selectedValues.includes(option.value) ? "bg-primary-600 border-primary-600" : "border-gray-200"
                    }`}
                  >
                    {selectedValues.includes(option.value) && (
                      <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 00-1.414 0L7.5 13.086l-2.793-2.793a1 1 0 00-1.414 1.414l3.5 3.5a1 1 0 001.414 0l8.5-8.5a1 1 0 000-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="ml-2">{option.label}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
